import { CircleInfoIcon, SendIcon } from "@xola/icons";
import { Alert, Checkbox, Drawer, flash, FormGroup, Label, SubmitButton, Switch } from "@xola/ui-kit";
import { useFormikContext } from "formik";
import { isEqual, uniqueId } from "lodash";
import React, { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { CommunicationChannelType, MessageFormatType, MessageTemplate, Permission, SelectionState } from "types";
import { api } from "../../../api/api";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useCollection } from "../../../hooks/useCollection";
import { IsGranted } from "../IsGranted";
import { SecondaryButton } from "../SecondaryButton";
import { XolaFormik } from "../XolaFormik";
import { ALLOWED_ATTACHMENT_TYPES, AttachmentField, MAX_ATTACHMENT_SIZE_BYTES } from "./AttachmentField";
import { ChannelMessageSection } from "./ChannelMessageSection";
import EmailForm from "./EmailForm";
import {
    buildChannelConfigs,
    buildRecipients,
    DrawerFormState,
    getFlashText,
    getInitialChannelIds,
    MessageDrawerChannels,
    MessageSendTo,
    OutgoingMessage,
    OutgoingMessageFormat,
    partitionChannels,
    validate,
} from "./messageDrawerHelpers";
import { ChannelSwitch } from "./MessageForm";
import { getDescription, RecipientsField } from "./RecipientsField";
import SmsForm from "./SmsForm";
import { TemplateSelect } from "./TemplateSelect";
import { TravelerMessage } from "./TravelersSelector";

export interface MessageDrawerProps {
    isOpen: boolean;
    description?: ReactNode;
    filters?: Record<string, unknown>;
    title?: string;
    to?: MessageSendTo[];
    channels?: MessageDrawerChannels;
    travelersList?: TravelerMessage[];
    children?: ReactNode;
    shouldShowEmailGuides?: boolean;
    onClose: (e?: any) => void;
    onContinue?: (e?: any) => void;
}

export const MessageDrawer = ({ isOpen, onClose, title, ...rest }: MessageDrawerProps) => {
    const [drawerId] = useState(() => uniqueId("messageDrawer"));
    const [popoverParentId] = useState(() => uniqueId("popoverParentId"));
    return (
        <Drawer
            size="xl"
            isOpen={isOpen}
            classNames={{ dialog: "z-40" }}
            content={
                <div id={drawerId} className="flex h-full flex-col px-4">
                    <div className="pt-4 pb-6 text-center">
                        <h2 className="text-2xl font-bold">{title ?? "Send Message"}</h2>
                    </div>
                    <div id={popoverParentId} className="flex-1 overflow-y-auto pb-4">
                        {isOpen && (
                            <MessageDrawerContent popoverParentId={popoverParentId} onClose={onClose} {...rest} />
                        )}
                    </div>
                </div>
            }
            onClose={onClose}
        />
    );
};

export type MessageDrawerContentProps = Omit<MessageDrawerProps, "isOpen" | "title"> & {
    popoverParentId: string;
};

export const MessageDrawerContent = ({
    filters,
    to,
    channels,
    children,
    onClose,
    onContinue,
    travelersList,
    shouldShowEmailGuides = true,
    description,
    popoverParentId,
}: MessageDrawerContentProps) => {
    const user = useUser();
    const seller = useSeller();
    const { isEmailAvailable = true, isPhoneAvailable = true, messagingPlugin, preferred } = channels ?? {};

    const [selectedTravelers, setSelectedTravelers] = useState<SelectionState>({ all: true, selected: [] });
    const [attachment, setAttachment] = useState<File>();
    const [attachmentError, setAttachmentError] = useState<string>();
    const [emailGuides, setEmailGuides] = useState(false);
    // Persists assetId across retries so a failed sendMessage doesn't re-upload the same file.
    const uploadedAssetRef = useRef<{ file: File; id: string } | null>(null);

    const { data: templates } = useCollection<MessageTemplate>("messageTemplates");

    const {
        mutateAsync: sendMessage,
        isLoading,
        isSuccess,
    } = useMutation(async (message: OutgoingMessage) => {
        await api.post(`/sellers/${seller.id}/messages`, message, { suppressErrorToast: [400] });
    });

    const enabledChannels = seller.preferences.communicationChannels?.enabledChannels ?? [];
    const isSmsAvailable = enabledChannels.includes(CommunicationChannelType.SMS) && isPhoneAvailable;
    const channelConfigs = useMemo(
        () => buildChannelConfigs({ isEmailAvailable, isSmsAvailable, messagingPlugin }),
        [isEmailAvailable, isSmsAvailable, messagingPlugin],
    );
    const initialChannelIds = useMemo(() => {
        return getInitialChannelIds(channelConfigs, preferred);
    }, [channelConfigs, preferred]);

    const [template, setTemplate] = useState<MessageTemplate | null>(null);
    const [editTemplate, setEditTemplate] = useState(false);

    const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        setAttachmentError(undefined);
        uploadedAssetRef.current = null;

        if (!file) {
            setAttachment(undefined);
            return;
        }

        if (file.size === 0) {
            setAttachmentError("The selected file is empty.");
            setAttachment(undefined);
            return;
        }

        if (!ALLOWED_ATTACHMENT_TYPES.has(file.type)) {
            setAttachmentError("Only images (JPEG, PNG, GIF, WebP) and PDF files are allowed.");
            setAttachment(undefined);
            return;
        }

        if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
            setAttachmentError("File must be smaller than 2 MB.");
            setAttachment(undefined);
            return;
        }

        setAttachment(file);
    };

    const uploadAttachment = async (file: File): Promise<string> => {
        // Reuse a prior successful upload for the same file so retries don't orphan storage.
        if (uploadedAssetRef.current?.file === file) {
            return uploadedAssetRef.current.id;
        }
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post<{ id: string }>("/assets", formData);
        uploadedAssetRef.current = { file, id: data.id };
        return data.id;
    };

    const handleSubmit = async (values: DrawerFormState) => {
        const { channels, email, sms } = values;
        const { selected, smsChannels, appChannels } = partitionChannels(channelConfigs, channels);
        const isEmailSelected = channels.includes(CommunicationChannelType.EMAIL);

        const assetId = attachment ? await uploadAttachment(attachment) : null;
        const mediaFormat: OutgoingMessageFormat | null = assetId
            ? { type: MessageFormatType.MEDIA, asset: { id: assetId } }
            : null;

        const formats: OutgoingMessageFormat[] = [];
        if (isEmailSelected) {
            formats.push({ type: MessageFormatType.EMAIL, subject: email.subject, body: email.body });
        }
        if (appChannels.length > 0 || smsChannels.length > 0) {
            formats.push({ type: MessageFormatType.TEXT, body: sms.body });
        }
        if (mediaFormat) {
            formats.push(mediaFormat);
        }

        const recipients = buildRecipients({ travelersList, selectedTravelers, to });

        const messageTemplate = {
            channels: [
                ...(isEmailSelected
                    ? [
                          {
                              type: CommunicationChannelType.EMAIL,
                              subject: email.subject,
                              body: email.body,
                              ...(mediaFormat ? { formats: [mediaFormat] } : {}),
                          },
                      ]
                    : []),
                ...appChannels.map((channel) => ({
                    type: channel.id,
                    body: sms.body,
                    ...(mediaFormat ? { formats: [mediaFormat] } : {}),
                })),
                ...smsChannels.map((channel) => ({ type: channel.id, body: sms.body })),
            ],
        };
        const f = emailGuides ? { ...filters, includeGuides: true } : filters;

        if (channels.length > 0) {
            try {
                await sendMessage({ filters: f, from: user.id, to: recipients, channels, formats, messageTemplate });
                flash.success(getFlashText(selected, selectedTravelers.all));
                onContinue?.();
                onClose();
            } catch (error: any) {
                if (error?.response?.status === 400) {
                    flash.danger("Messages not sent: Recipient contact info is missing.");
                } else {
                    throw error;
                }
            }
        }
    };

    const initialValues = useMemo<DrawerFormState>(() => {
        return {
            sms: { body: "" },
            channels: initialChannelIds,
            email: { subject: "", body: "" },
        };
    }, [initialChannelIds]);

    const hasNoRecipients = !!travelersList && selectedTravelers.selected.length === 0 && !selectedTravelers.all;

    return (
        <XolaFormik
            initialValues={initialValues}
            validate={(values: DrawerFormState) => validate(values, template, channelConfigs, !!attachment)}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit: submitForm, values, setFieldValue }) => {
                const { plainText: plainTextChannels, selected } = partitionChannels(channelConfigs, values.channels);
                const isEmailSelected = values.channels.includes(CommunicationChannelType.EMAIL);
                const showAttachment = selected.some((config) => config.supportsAttachments);
                const recipientsDescription = description ?? getDescription(selectedTravelers, travelersList ?? []);

                const toggleChannel = (id: string, checked: boolean) =>
                    setFieldValue(
                        "channels",
                        checked ? [...values.channels, id] : values.channels.filter((channelId) => channelId !== id),
                    );

                const applyTemplate = (template: MessageTemplate | null) => {
                    setTemplate(template);
                    setEditTemplate(false);
                    setFieldValue("email", {
                        subject: template?.email?.subject ?? "",
                        body: template?.email?.body ?? "",
                    });
                    setFieldValue("sms", { body: template?.sms?.body ?? "" });

                    if (template) {
                        const templateHasContent: Record<string, boolean> = {
                            email: !!template.email,
                            sms: !!template.sms?.body,
                        };
                        const hasAnyContent = Object.values(templateHasContent).some(Boolean);
                        if (hasAnyContent) {
                            const activeChannels = channelConfigs
                                .filter((config) => templateHasContent[config.templateField])
                                .map((config) => config.id);
                            setFieldValue("channels", activeChannels);
                        }
                    } else {
                        setFieldValue("channels", initialChannelIds);
                    }
                };

                return (
                    <div className="flex flex-col gap-4">
                        <InitialChannelsSync initialChannelIds={initialChannelIds} />
                        <fieldset disabled={isLoading} className="flex min-w-0 flex-col gap-4 border-0 p-0">
                            {children}

                            {travelersList && (
                                <RecipientsField
                                    travelersList={travelersList}
                                    value={selectedTravelers}
                                    popoverParentId={popoverParentId}
                                    onChange={setSelectedTravelers}
                                />
                            )}

                            {recipientsDescription && (
                                <Alert color={hasNoRecipients ? "danger" : "primary"}>
                                    <CircleInfoIcon size="large" />
                                    <span className="ml-2">{recipientsDescription}</span>
                                </Alert>
                            )}

                            {shouldShowEmailGuides && (
                                <IsGranted permissions={Permission.RESOURCES_GUIDES_ALL_VIEW}>
                                    <div className="flex items-center gap-1">
                                        <Switch isChecked={emailGuides} onChange={() => setEmailGuides(!emailGuides)} />
                                        <span>Send to assigned guides</span>
                                    </div>
                                </IsGranted>
                            )}

                            <div className="space-y-2">
                                <Label>Communication Channels</Label>
                                <div className="ml-1 flex items-center gap-2">
                                    {channelConfigs.map((channel) => (
                                        <Checkbox
                                            key={channel.id}
                                            label={channel.label}
                                            checked={values.channels.includes(channel.id)}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                toggleChannel(channel.id, e.target.checked)
                                            }
                                        />
                                    ))}
                                </div>
                            </div>

                            {channelConfigs.length > 0 && (
                                <FormGroup>
                                    <Label>Template</Label>
                                    <TemplateSelect
                                        className="flex-1"
                                        value={template}
                                        templates={templates}
                                        dataTestId="message-drawer-template-select"
                                        onChange={applyTemplate}
                                    />
                                </FormGroup>
                            )}

                            {template && (isEmailSelected || plainTextChannels.length > 0) && (
                                <ChannelSwitch
                                    label="Edit Message"
                                    isChecked={editTemplate}
                                    onChange={setEditTemplate}
                                />
                            )}

                            {plainTextChannels.length > 0 && (
                                <ChannelMessageSection title="Plain Text Message" channels={plainTextChannels}>
                                    <SmsForm template={template} editTemplate={editTemplate} />
                                </ChannelMessageSection>
                            )}

                            {isEmailSelected && (
                                <ChannelMessageSection
                                    title="Rich Text Message"
                                    channels={channelConfigs.filter(
                                        (config) => config.id === CommunicationChannelType.EMAIL,
                                    )}
                                >
                                    <EmailForm showSubject template={template} editTemplate={editTemplate} />
                                </ChannelMessageSection>
                            )}

                            {showAttachment && (
                                <AttachmentField
                                    attachment={attachment}
                                    attachmentError={attachmentError}
                                    onAttachmentChange={handleAttachmentChange}
                                    onAttachmentRemove={() => {
                                        setAttachment(undefined);
                                        setAttachmentError(undefined);
                                        uploadedAssetRef.current = null;
                                    }}
                                />
                            )}
                        </fieldset>

                        <div className="flex justify-end gap-3">
                            <SubmitButton
                                isLoading={isLoading}
                                isSuccess={isSuccess}
                                icon={<SendIcon />}
                                disabled={values.channels.length === 0 || hasNoRecipients}
                                onClick={submitForm}
                            >
                                Send
                            </SubmitButton>
                            <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        </div>
                    </div>
                );
            }}
        </XolaFormik>
    );
};

/**
 * Formik reads `initialValues` once, so a messaging plugin that resolves after the drawer opens would leave the
 * fallback channels checked. Follow the new defaults only while the user has not changed the selection, and move the
 * baseline too on a pristine form so the leave prompt does not fire for a drawer nobody touched.
 */
const InitialChannelsSync = ({ initialChannelIds }: { initialChannelIds: string[] }) => {
    const { values, dirty, setFieldValue, resetForm } = useFormikContext<DrawerFormState>();
    const appliedIdsRef = useRef(initialChannelIds);

    useEffect(() => {
        const appliedIds = appliedIdsRef.current;
        if (isEqual(appliedIds, initialChannelIds)) {
            return;
        }

        appliedIdsRef.current = initialChannelIds;
        if (!isEqual(values.channels, appliedIds)) {
            return;
        }

        if (dirty) {
            setFieldValue("channels", initialChannelIds);
        } else {
            resetForm({ values: { ...values, channels: initialChannelIds } });
        }
    }, [initialChannelIds, values, dirty, setFieldValue, resetForm]);

    return null;
};
